import Model, { attr, belongsTo } from '@ember-data/model'
import { Type } from '@warp-drive/core-types/symbols'
import type BookLibraryBook from 'teacher-ui-client/models/book-library/book'
import type StringTransform from 'teacher-ui-client/transforms/string'
import type NumberTransform from 'teacher-ui-client/transforms/number'
import type BooleanTransform from 'teacher-ui-client/transforms/boolean'
import type ReadonlyObjectTransform from 'teacher-ui-client/transforms/readonly-object'

export interface Chapter {
  name: string
  /**
   * Whether chapter has an end of chapter quiz
   */
  has_quiz: boolean
}

export interface HasBookNotes {
  reading?: boolean
  maths?: boolean
  writing?: boolean
}

interface CoverImage {
  path: string
  width: number
  height: number
}

/**
 * A book metadata record directly associated to a book record
 *
 * It is split from the book because these are the localised attributes for the book
 */

export default class BookLibraryBookMetadata extends Model {
  // sync relationship, book record will always be loaded along with the metadata record
  @belongsTo<BookLibraryBook>('book-library/book', { async: false, inverse: 'bookMetadata' })
  declare book?: BookLibraryBook

  @attr<StringTransform>('string') declare name: string

  @attr<StringTransform>('string') declare description: string

  @attr<NumberTransform>('number') declare pages: number

  @attr() declare chapters: Chapter[]

  @attr<NumberTransform>('number') declare wordCount: number

  /**
   * A flag that is true if the book has any audio
   */
  @attr<BooleanTransform>('boolean') declare audio: boolean

  /**
   * A flag that is true if the book has page narration audio
   */
  @attr<BooleanTransform>('boolean') declare narrationAudio: boolean

  @attr<BooleanTransform>('boolean') declare hasResources: boolean

  @attr() declare hasBookNotes?: HasBookNotes

  /**
   * Whether an end of book quiz is present for this book
   */
  @attr<BooleanTransform>('boolean') declare hasQuiz: boolean

  // { path, width, height }
  @attr<ReadonlyObjectTransform>('readonly-object') declare coverPreviewImg: CoverImage

  // { path, width, height } - higher res than the preview image
  @attr<ReadonlyObjectTransform>('readonly-object') declare coverFullImg: CoverImage

  /* Each attribute here should equal the camelized form of a reading scale key */
  @attr<NumberTransform>('number') declare blakeReadingLevel: number

  // Will be a single lexile level like "650L"
  @attr<StringTransform>('string') declare lexileLevel: string

  // Will be a range like "600L-700L", used for grouping books in search results
  @attr<StringTransform>('string') declare lexileRange: string

  @attr<StringTransform>('string') declare readingAge: string

  @attr<StringTransform>('string') declare yearLevel: string

  // eslint-disable-next-line customPlugin/prefer-american-color
  @attr<StringTransform>('string') declare bookColour: string

  @attr<StringTransform>('string') declare dra: string

  @attr<NumberTransform>('number') declare rrLevel: number

  // Fountas & Pinnell reading levels
  @attr<StringTransform>('string') declare fpAz: string

  @attr<ReadonlyObjectTransform>('readonly-object') declare fpPeaks?: number[];

  [Type] = 'book-library/book-metadata' as const
}
